<template>
    <Head>
        <link
            rel="canonical"
            :href="$page.props.ziggy.location"
        />
        <title v-if="$page.props.active_faq">FAQ: {{ $page.props.active_faq.question }}</title>
        <title v-if="!$page.props.active_faq">FAQ: frequently asked questions</title>
        <meta
            property="og:title"
            head-key="og:title"
            :content="
                $page.props.active_faq
                    ? `FAQ: ${$page.props.active_faq.question_e}`
                    : 'FAQ: frequently asked questions'
            "
        />

        <meta
            v-if="$page.props.active_faq"
            name="description"
            head-key="description"
            :content="$page.props.active_faq.question"
        />

        <meta
            v-if="$page.props.active_faq"
            property="og:description"
            :content="$page.props.active_faq.question"
        />
    </Head>

    <AppLayout>
        <template #header><h1>FAQ</h1></template>
        <Container>
            <div class="bg-white">
                <div class="mx-auto max-w-7xl px-6 py-8 lg:px-8">
                    <div class="divide-0 lg:grid lg:grid-cols-3 lg:gap-12 lg:divide-x">
                        <div class="lg:col-span-1">
                            <h2
                                class="hidden text-2xl font-bold leading-10 tracking-tight text-gray-900 lg:block"
                            >
                                Frequently Asked Questions
                            </h2>
                            <p class="mb-10 mt-4 hidden text-base leading-7 text-gray-600 lg:block">
                                Can’t find the answer you’re looking for? Reach out through any of our
                                <Link href="/contact-us">support channels</Link>
                            </p>

                            <Disclosure
                                v-slot="{ open, close }"
                                as="div"
                                class="mb-14 overflow-hidden rounded-lg bg-white shadow-lg lg:hidden"
                            >
                                <DisclosureButton
                                    as="div"
                                    class="flex cursor-pointer flex-row items-center justify-between p-3 sm:p-4"
                                >
                                    <span class="px-1 text-base font-semibold leading-7 text-gray-900"
                                        >Choose a question</span
                                    >

                                    <span class="ml-6 flex h-7 items-center">
                                        <svg
                                            :class="open ? 'rotate-180 duration-500' : 'rotate-0 duration-500'"
                                            class="size-6 fill-current text-blue-700"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                d="M13.962,8.885l-3.736,3.739c-0.086,0.086-0.201,0.13-0.314,0.13S9.686,12.71,9.6,12.624l-3.562-3.56C5.863,8.892,5.863,8.611,6.036,8.438c0.175-0.173,0.454-0.173,0.626,0l3.25,3.247l3.426-3.424c0.173-0.172,0.451-0.172,0.624,0C14.137,8.434,14.137,8.712,13.962,8.885 M18.406,10c0,4.644-3.763,8.406-8.406,8.406S1.594,14.644,1.594,10S5.356,1.594,10,1.594S18.406,5.356,18.406,10 M17.521,10c0-4.148-3.373-7.521-7.521-7.521c-4.148,0-7.521,3.374-7.521,7.521c0,4.147,3.374,7.521,7.521,7.521C14.148,17.521,17.521,14.147,17.521,10"
                                            ></path>
                                        </svg>
                                    </span>
                                </DisclosureButton>
                                <transition
                                    :show="open"
                                    enter-active-class="duration-300 transition-all"
                                    enter-from-class="max-h-0"
                                    enter-to-class="max-h-64"
                                    leave-active-class="duration-300 transition-all"
                                    leave-from-class="max-h-64"
                                    leave-to-class="max-h-0"
                                >
                                    <DisclosurePanel
                                        as="div"
                                        class="max-h-0 overflow-hidden border-l-2 border-blue-600"
                                    >
                                        <ul class="ml-8 list-disc">
                                            <li
                                                v-for="faq_group in $page.props.faq_categories"
                                                :key="faq_group.title"
                                            >
                                                <h3>{{ faq_group.title }}</h3>
                                                <ul class="mb-6 ml-8 mt-2 list-disc space-y-2">
                                                    <li
                                                        v-for="faq in faq_group.faqs"
                                                        :key="faq.id"
                                                    >
                                                        <Link
                                                            :class="
                                                                $page.props.active_faq?.id == faq.id
                                                                    ? 'font-bold'
                                                                    : 'font-normal'
                                                            "
                                                            :href="
                                                                route('faq', {
                                                                    faq: mySlugify(faq.question) + '-' + faq.id,
                                                                })
                                                            "
                                                        >
                                                            {{ faq.question }}
                                                        </Link>
                                                    </li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </DisclosurePanel>
                                </transition>
                            </Disclosure>

                            <ul class="hidden gap-4 lg:grid">
                                <li
                                    v-for="faq_group in $page.props.faq_categories"
                                    :key="faq_group.title"
                                >
                                    <h3>{{ faq_group.title }}</h3>
                                    <ul class="ml-8 mt-2 list-disc space-y-2">
                                        <li
                                            v-for="faq in faq_group.faqs"
                                            :key="faq.id"
                                        >
                                            <Link
                                                :class="
                                                    $page.props.active_faq?.id == faq.id
                                                        ? 'font-bold'
                                                        : 'font-normal'
                                                "
                                                :href="
                                                    route('faq', {
                                                        faq: mySlugify(faq.question) + '-' + faq.id,
                                                    })
                                                "
                                            >
                                                {{ faq.question }}
                                            </Link>
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                        </div>
                        <div class="mt-10 lg:col-span-2 lg:mt-0 lg:pl-6">
                            <div v-if="$page.props.active_faq">
                                <h4 class="">
                                    {{ $page.props.active_faq.question }}
                                </h4>
                                <div class="pb-4 text-sm text-gray-400">
                                    Updated
                                    <time
                                        itemprop="dateModified"
                                        :datetime="
                                            new Date($page.props.active_faq.filemtime * 1000).toISOString()
                                        "
                                        :title="
                                            new Date($page.props.active_faq.filemtime * 1000).toLocaleString()
                                        "
                                        >{{
                                            new Date(
                                                $page.props.active_faq.filemtime * 1000,
                                            ).toLocaleDateString()
                                        }}
                                    </time>
                                </div>
                                <div
                                    class="prose mt-2 max-w-none text-base leading-7 text-gray-600"
                                    v-html="$page.props.active_faq.answer"
                                ></div>
                            </div>
                            <div
                                v-else
                                class="hidden lg:block"
                            >
                                <a href="/faq/how-to-participate-in-the-event-25"
                                    >How to <strong>participate</strong> in the event?</a
                                >
                                <br />
                                <br />
                                <a href="/faq/how-do-i-organize-my-first-event-63"
                                    >How do I start <strong>organizing</strong> my first event?</a
                                >
                                <br />
                                <br />
                                <a href="/faq/how-to-create-your-first-digital-rally-roadbook-54"
                                    >How to create your first Digital Rally Roadbook?</a
                                >
                                <br />
                                <br />

                                Browse more questions on the left.
                            </div>
                        </div>

                        <div class="my-10 text-base leading-7 text-gray-600 lg:hidden">
                            <hr />
                            <br />
                            Can’t find the answer you’re looking for? Reach out through any of our
                            <Link href="/contact-us">support channels</Link>
                            <br />
                            <br />
                            <br />
                            <hr />
                            <br />
                            <div>
                                <strong>Popular questions:</strong> <br />
                                <br />
                                <ul class="ml-4 list-disc space-y-3">
                                    <li>
                                        <a href="/faq/how-to-participate-in-the-event-25"
                                            >How to <strong>participate</strong> in the event?</a
                                        >
                                    </li>
                                    <li>
                                        <a href="/faq/how-do-i-organize-my-first-event-63"
                                            >How do I start <strong>organizing</strong> my first event?</a
                                        >
                                    </li>
                                    <li>
                                        <a href="/faq/how-to-create-your-first-digital-rally-roadbook-54"
                                            >How to create your first Digital Rally Roadbook?</a
                                        >
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    </AppLayout>
</template>

<script setup>
import { Head, Link } from "@inertiajs/vue3";
import AppLayout from "../Layouts/AppLayout.vue";
import Container from "../Components/Container.vue";
import slugify from "slugify";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";

const props = defineProps({
    faq_categories: Object,
    active_faq: Object,
});

function mySlugify(string) {
    return slugify(string, { lower: true, remove: /[*?+/,~.()'"!:@]/g });
}
</script>
